import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKey, ShieldCheck, FileLock, HandHeart, ArrowLeft, SignOut } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { getHomePathForRole } from "../modules/registry";
import { LOGO_URL, SUPPORT_EMAIL } from "../constants/brand";
import { PageWatermark } from "../components/shared/PageWatermark";

const PILLARS = [
  {
    icon: LockKey,
    title: "256-bit SSL Encryption",
    description: "Every connection between your browser and IGC's servers is secured with industry-standard 256-bit SSL/TLS encryption — the same level of protection used by major banks.",
  },
  {
    icon: ShieldCheck,
    title: "Bank-Level Security",
    description: "Passwords are hashed with bcrypt, sessions use httpOnly signed tokens, and every account is protected against brute-force login attempts with automatic lockouts.",
  },
  {
    icon: FileLock,
    title: "Data Privacy Policy",
    description: "Your financial and legal information is strictly isolated by account — no client can ever view another client's data. Information is never sold or shared with third parties.",
  },
  {
    icon: HandHeart,
    title: "Confidentiality Commitment",
    description: "IGC's team is bound by strict confidentiality standards. Your business details, trust structures, and funding applications are handled with the same discretion as our private wealth clients.",
  },
];

export default function SecurityCenterPage() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-igc-bg p-10 relative" data-testid="security-center-page">
      <PageWatermark />
      <div className="relative z-[1]">
      <div className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-14 h-14 object-contain igc-glow" />
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight">Security & Compliance</h1>
            <p className="text-igc-muted text-sm">Your data, protected at every layer.</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <Link
            to={user ? getHomePathForRole(user.role) : "/login"}
            data-testid="security-center-back-link"
            title="Returns you to your dashboard."
            className="flex items-center gap-2 text-igc-muted hover:text-igc-goldText transition-colors text-sm"
          >
            <ArrowLeft size={16} weight="bold" /> Back to Dashboard
          </Link>
          {user && (
            <button
              data-testid="security-center-logout-button"
              onClick={logout}
              title="Signs you out of the portal completely."
              className="flex items-center gap-2 text-igc-muted hover:text-igc-danger transition-colors text-sm"
            >
              <SignOut size={16} weight="bold" /> Sign Out
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-igc-surface border border-igc-gold/15 p-8"
              data-testid={`security-pillar-${i}`}
              title={p.description}
            >
              <Icon size={30} weight="duotone" className="text-igc-goldText mb-4" />
              <p className="font-serif text-lg text-igc-cream mb-2">{p.title}</p>
              <p className="text-igc-muted text-sm leading-relaxed">{p.description}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-10 max-w-4xl bg-igc-surface border border-igc-gold/15 p-6 flex items-center gap-3" data-testid="security-center-footer-badge">
        <ShieldCheck size={20} weight="fill" className="text-igc-success" />
        <p className="text-igc-muted text-sm">
          256-bit SSL Secured &middot; Bank-Level Security &middot; Questions about how we protect your data? Contact <span className="text-igc-goldText">{SUPPORT_EMAIL}</span>
        </p>
      </div>
      </div>
    </div>
  );
}
